import type { StepConfig, FormValues } from '../types';

/**
 * Gets the next step ID from step configuration
 */
export function getNextStepIdFromConfig(
  currentStep: StepConfig,
  values: FormValues
): string | null {
  const next = currentStep.next;
  if (!next) return null;

  if (next.type === 'submit') return null;

  if (next.type === 'static') {
    return next.stepId ?? null;
  }

  if (next.type === 'byField') {
    const fieldId = next.fieldId ?? '';
    const v = values[fieldId];
    const mapping = next.cases ?? {};
    if (v in mapping) {
      return mapping[v];
    }
    return next.defaultStepId ?? null;
  }

  return null;
}

/**
 * Gets the previous step ID from step configuration
 */
export function getPreviousStepIdFromConfig(
  currentStep: StepConfig,
  values: FormValues
): string | null {
  const back = currentStep.back;
  if (!back) return null;

  if (back.type === 'previous') return null; // Will use list-based navigation

  if (back.type === 'static') {
    return back.stepId ?? null;
  }

  if (back.type === 'byField') {
    const fieldId = back.fieldId ?? '';
    const v = values[fieldId];
    const mapping = back.cases ?? {};
    if (v in mapping) {
      return mapping[v];
    }
    return back.defaultStepId ?? null;
  }

  return null;
}

/**
 * Gets the next visible step ID from the visible steps list
 */
export function getNextVisibleStepIdInList(
  visibleSteps: StepConfig[],
  currentStepId: string
): string | null {
  const index = visibleSteps.findIndex((s) => s.id === currentStepId);
  if (index === -1) return visibleSteps[0]?.id ?? null;
  if (index + 1 < visibleSteps.length) {
    return visibleSteps[index + 1].id;
  }
  return null;
}

/**
 * Gets the previous visible step ID from the visible steps list
 */
export function getPreviousVisibleStepIdInList(
  visibleSteps: StepConfig[],
  currentStepId: string
): string | null {
  const index = visibleSteps.findIndex((s) => s.id === currentStepId);
  if (index === -1) return null;
  if (index > 0) {
    return visibleSteps[index - 1].id;
  }
  return null;
}
